import { Component, Input, ViewChild } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { SpeciesService } from 'src/app/application/service/SpeciesService';
import { Species, SpeciesCreateRequest, SpeciesUpdateRequest } from 'src/app/domain/models/Species';
import { Router } from '@angular/router';
import { Routes } from 'src/app/infrastructure/routes';
import { FileUpload } from 'primeng/fileupload';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';

@Component({
  selector: 'app-form-manage-species',
  templateUrl: './form-manage-species.component.html',
  styleUrls: ['./form-manage-species.component.css'],

})
export class FormManageSpeciesComponent {

  @Input() lang: string | undefined = 'en'
  @Input() species: Species | undefined

  @ViewChild('fileUploaderVariety') fileUploaderVariety!: FileUpload;

  submit: boolean = false
  loading: boolean = false
  uploadedFiles: any[] = []
  previewUrl!: any;

  formGroup: FormGroup = this._formBuilder.group({
    name: ['', Validators.required],
    nameEs: ['', Validators.required],
    image: [''],
    varieties: this._formBuilder.array([])
  })

  varietyForm: FormGroup = this._formBuilder.group({
    name: [''],
    icon: [null]
  })

  constructor(
    private sanitizer: DomSanitizer,
    private readonly _router: Router,
    private readonly _formBuilder: FormBuilder,
    private readonly _messageService: MessageService,
    private readonly _speciesService: SpeciesService,
    private readonly translate: TranslateService
  ) {}

  ngOnInit() {
    this.loadDetail()
  }

  async loadDetail() {
    if (this.species !== undefined) {
      this.formGroup.patchValue({
        name: this.species?.name,
        nameEs: this.species?.nameEs,
        image: this.species?.imageUrl
      })
    }
  }

  onSelect(event: any) {
    this.uploadedFiles = []
    for (let file of event.files) {
      this.uploadedFiles.push(file);
    }
  }

  save() {
    this.submit = true;
    this.loading = true;

    if (this.formGroup.valid) {
      if (!this.uploadedFiles[0] && !this.species) {
        this.loading = false;
        this._messageService.add({
          severity: 'error',
          summary: this.translate.instant('AdminSpeciesManagement.Messages.FailedActionTitle'),
          detail: this.translate.instant('AdminSpeciesManagement.Messages.ErrorEmptyImageTex')
        })
      } else {
        const speciesRequest: SpeciesCreateRequest = {
          name: this.formGroup.controls["name"].value,
          nameEs: this.formGroup.controls["nameEs"].value,
          imgGenericIcon: this.uploadedFiles[0],
          varieties: this.varieties.value
        }

        let request = null
        if (this.species !== undefined) {
          const speciesUpdateRequest: SpeciesUpdateRequest = {
            ...speciesRequest,
            id: this.species.id!,
            nameEs: this.formGroup.controls["nameEs"].value,
            imgGenericIcon: this.uploadedFiles[0] ?? null
          }
          request = this._speciesService.update(speciesUpdateRequest)
        }
        else {
          request = this._speciesService.create(speciesRequest)
        }

        request?.subscribe({
          next: () => {
            this.loading = false;
            this._messageService.add({
              severity: 'success',
              summary: this.translate.instant('AdminSpeciesManagement.Messages.SuccessfulActionTitle'),
              detail: this.translate.instant('AdminSpeciesManagement.Messages.SuccessCreatingSpecieText')
            })
            this._router.navigate(['/' + Routes.adminSpecies])
          },
          error: () => {
            this.loading = false;
            this._messageService.add({
              severity: 'error',
              summary: this.translate.instant('AdminSpeciesManagement.Messages.FailedActionTitle'),
              detail: this.translate.instant('AdminSpeciesManagement.Messages.ErrorCreatingSpecieText')
            })
          }
        })
      }
    }
    else {
      this.loading = false;
      this._messageService.add({
        severity: 'error',
        summary: this.translate.instant('AdminSpeciesManagement.Messages.FailedActionTitle'),
        detail: this.translate.instant('AdminSpeciesManagement.Messages.ErrorFormText')
      })
    }
  }

  removeImage() {
    this.formGroup.get('image')?.setValue('')
  }

  getErrorMessage(): string {
    return this.translate.instant('AdminSpeciesManagement.Messages.MandatoryFieldText');
  }

  get varieties(): FormArray {
    return this.formGroup.get('varieties') as FormArray;
  }

  onFileSelect(event: any) {

  const file = event.currentFiles[0];

  if (file) {
    this.varietyForm.patchValue({
      icon: file
    });

    const objectUrl = URL.createObjectURL(file);
    this.previewUrl = this.sanitizer.bypassSecurityTrustUrl(objectUrl);
  }

}

  addVariety() {
    const { name, icon } = this.varietyForm.value;

    if (!name || !icon) return;

    this.varieties.push(
      this._formBuilder.group({
        name: name,
        icon: icon,
        preview: this.previewUrl
      })
    );

    this.previewUrl = null;
    this.varietyForm.reset();
    this.fileUploaderVariety.clear();
  }

  removeVariety(index: number) {
    this.varieties.removeAt(index);
  }

}
